import React, { useEffect, useState } from 'react';
import '../../../assets/css/overview-updated.css';
import Select from 'react-select';
import APIServices from '../../../services/APIServices';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import { fetchProjectTeamMembers } from '../../../utils/fetchData';
import { calculateDateDiff, formatDate, selectLoader, validateTextInput } from '../../../utils/Helpers';
import appToast, { TOAST_TYPE } from '../../../utils/AppToast';
import { useStore } from '../../../hooks/useStore';
import UpdateDaysSlider from '../../../components/UpdateDaysSlider';
import user from '../../../assets/img/nick-user2.png';

const UpdateProjectForm = ({ projectId, closeModal }) => {
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('ontime');
  const [errors, setErrors] = useState({});
  const [updatedDay, setUpdatedDay] = useState(0);
  const [projectData, setProjectData] = useState(null);
  const [projectProgress, setProgress] = useState(0);
  const [clientOptions, setClientOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);

  // Single state object to hold all form fields
  const [formData, setFormData] = useState({
    projectId: '',
    status: '',
    daysLate: 0,
    daysEarly: 0,
    updateNote: '',
    // notifyClient: false,
    clientRecipients: [],
    teamRecipients: []
  });

  useEffect(() => {
    if (projectData) {
      let latestUpdates = projectData?.latestUpdate[0];
      setFormData((prevData) => ({
        ...prevData,
        status: latestUpdates?.updateStatus?._id || formData.status
      }));
      setProgress(projectData.progress.toFixed(0));
      if (latestUpdates?.daysEarly > 0) {
        setUpdatedDay(-latestUpdates?.daysEarly);
      } else {
        setUpdatedDay(latestUpdates?.daysLate);
      }
      if (latestUpdates?.updateStatus?.value) setCurrentStatus(latestUpdates?.updateStatus?.value);
    }
  }, [projectData]);

  const fetchStatusOptions = async () => {
    try {
      const response = await APIServices.get(`/enum?item_type=projectupdate`);
      if (response.length > 0) {
        setStatusOptions(response);
        let onTimeStatus = response.find((x) => x.value == 'ontime');
        setFormData((prevData) => ({
          ...prevData,
          status: onTimeStatus?._id
        }));
        currentStatus(onTimeStatus.value);
      }
    } catch (error) {
      console.error('Error fetching status options:', error);
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchStatusOptions();
      fetchProjectData();
      fetchProjectTeamMembers(projectId, setClientOptions, 'user2');
      fetchProjectTeamMembers(projectId, setTeamOptions, 'user1'); // team
    }
  }, [projectId]);

  // Fetching project data from the backend
  const fetchProjectData = async () => {
    try {
      // Using axios or fetch to get data from your API
      const response = await APIServices.get(`/project/${projectId}`); // API call with project ID
      console.log(response);
      if (response.projectDetail) {
        setProjectData(response.projectDetail); // Set the response data in state
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false); // Stop loading even if there is an error
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
    }
  };

  const getStatusId = () => {
    if (updatedDay > 0) {
      return statusOptions.find((x) => x.value == 'late')._id;
    } else if (updatedDay < 0) {
      return statusOptions.find((x) => x.value == 'early')._id;
    } else {
      return statusOptions.find((x) => x.value == 'ontime')._id;
    }
  };

  const validateForm = (isRecurring = false, isAutoRenew = false) => {
    const newErrors = {};

    // if (!formData.milestoneId) newErrors.milestoneId = "Milestone is required.";
    // if (!formData.status) newErrors.status = "Status is required.";
    // if (currentStatus == "late" && !formData.daysLate)
    //   newErrors.numberofDays = "Please Enter Number of days.";
    // if (currentStatus == "early" && !formData.daysEarly)
    //   newErrors.numberofDays = "Please Enter Number of days early.";
    if (isRecurring && isAutoRenew) {
      if (!validateTextInput(formData.updateNote).isValid) {
        if (!validateTextInput(formData.updateNote).cleanedValue) {
          newErrors.updateNote = 'Update note cannot be empty.';
        } else {
          newErrors.updateNote = validateTextInput(formData.updateNote).message;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm(projectData?.isRecurring, projectData?.isAutoRenew)) {
      setSubmitLoading(true);
      const payload = {
        projectId: projectId,
        // milestoneId: formData.milestoneId,
        updateStatus: getStatusId() || '',
        daysLate: updatedDay > 0 ? updatedDay : 0,
        daysEarly: updatedDay < 0 ? Math.abs(updatedDay) : 0,
        // fromPercentage: formData.fromPercentage,
        // toPercentage: formData.toPercentage,
        updateNote: formData.updateNote,
        // notifyClient: formData.notifyClient,
        clientRecipients: formData.clientRecipients,
        teamRecipients: formData.teamRecipients
      };
      try {
        const response = await APIServices.post('/project-updates', payload);
        if (response.message) {
          appToast(response.message, TOAST_TYPE.SUCCESS);
          closeModal();
        }
      } catch (err) {
        console.error('Error while verifying email', err);
      } finally {
        setSubmitLoading(false);
      }
    } else {
      console.log('Form has validation errors.');
    }
  };

  const handleRecipientToggle = (userId) => {
    setFormData((prev) => {
      const alreadySelected = prev.clientRecipients.includes(userId);

      return {
        ...prev,
        clientRecipients: alreadySelected
          ? prev.clientRecipients.filter((id) => id !== userId)
          : [...prev.clientRecipients, userId]
      };
    });
  };

  const handleSelectAllRecipients = () => {
    const allIds = clientOptions.map((client) => client.value);

    const isAllSelected = allIds.length > 0 && allIds.every((id) => formData.clientRecipients.includes(id));

    setFormData((prev) => ({
      ...prev,
      clientRecipients: isAllSelected ? [] : allIds
    }));
  };

  const handleTeamRecipientToggle = (userId) => {
    setFormData((prev) => {
      const alreadySelected = prev.teamRecipients.includes(userId);

      return {
        ...prev,
        teamRecipients: alreadySelected
          ? prev.teamRecipients.filter((id) => id !== userId)
          : [...prev.teamRecipients, userId]
      };
    });
  };

  const handleSelectAllTeamRecipients = () => {
    const allIds = teamOptions.map((team) => team.value);

    const isAllSelected = allIds.length > 0 && allIds.every((id) => formData.teamRecipients.includes(id));

    setFormData((prev) => ({
      ...prev,
      teamRecipients: isAllSelected ? [] : allIds
    }));
  };

  if (loading) {
    return <div className="nodata-loader">{selectLoader(70)}</div>;
  }

  return (
    <div>
      <p className="cotent-text">Last updated 3 days ago - Website redesign</p>
      <div className="updatemodal-body">
        <div className="updatemodal updatemodal-leftContent">
          {projectData?.isRecurring === true && projectData?.isAutoRenew === true ? (
            ''
          ) : (
            <UpdateDaysSlider projectDetails={projectData} updatedDay={updatedDay} setUpdatedDay={setUpdatedDay} />
          )}

          <div className="row mt-3">
            <div className="col-lg-12">
              <div className="form-group">
                <label className="label-content">What’s the latest update?</label>
                <textarea
                  style={{ minHeight: '120px', maxHeight: '320px' }}
                  className={`form-control ${errors.updateNote ? 'form-invalid' : ''}`}
                  name="updateNote"
                  value={formData.updateNote}
                  // onChange={handleInputChange}
                  onChange={(e) => {
                    handleInputChange(e);
                    e.target.style.height = 'auto'; // Reset the height
                    e.target.style.height = `${e.target.scrollHeight + 2}px`; // Set the height to fit content
                  }}
                  placeholder="Add a brief project update (e.g., milestone reached, delay reason, next step)."></textarea>
                {errors.updateNote && <p className="error-message ml-2">{errors.updateNote}</p>}
              </div>
            </div>
          </div>

          <button className="btn-black" onClick={handleSubmit}>
            {!submitLoading ? 'Update' : selectLoader(35)}
          </button>
        </div>
        <div className="updatedSignOff-righttSec updatemodal-rightContent">
          <div className="updatemodal-div">
            <div className="client-name">
              <h4>Notify {projectData?.type === 'internal' ? 'Team' : 'Client'}</h4>
            </div>

            <div className="list">
              {/* Select All */}
              <div className="item">
                <div className="left">
                  <div className="name">All</div>
                </div>

                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={
                      clientOptions.length > 0 &&
                      clientOptions.every((client) => formData.clientRecipients.includes(client.value))
                    }
                    onChange={handleSelectAllRecipients}
                  />
                  <span></span>
                </label>
              </div>

              {/* Dynamic Users */}
              {clientOptions.map((client) => (
                <div className="item" key={client.value}>
                  <div className="left">
                    <img src={client.profilePic || user} alt="User" className="avatar" />

                    <div className="name">{client.label}</div>
                  </div>

                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.clientRecipients.includes(client.value)}
                      onChange={() => handleRecipientToggle(client.value)}
                    />
                    <span></span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="updatemodal-div mt-3">
            <div className="client-name">
              <h4>Notify Team</h4>
            </div>

            <div className="list">
              {/* Select All */}
              <div className="item">
                <div className="left">
                  <div className="name">All</div>
                </div>

                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={
                      teamOptions.length > 0 &&
                      teamOptions.every((client) => formData.teamRecipients.includes(client.value))
                    }
                    onChange={handleSelectAllTeamRecipients}
                  />
                  <span></span>
                </label>
              </div>

              {/* Dynamic Users */}
              {teamOptions.map((client) => (
                <div className="item" key={client.value}>
                  <div className="left">
                    <img src={client.profilePic || user} alt="User" className="avatar" />

                    <div className="name">{client.label}</div>
                  </div>

                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.teamRecipients.includes(client.value)}
                      onChange={() => handleTeamRecipientToggle(client.value)}
                    />
                    <span></span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateProjectForm;
